/**
 * CanvasMappingEditor — Entry component for the MAPPING CANVAS (field-level detail).
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │  THIS IS THE MAPPING CANVAS.                                        │
 * │  It shows FIELD-LEVEL nodes: source fields → mapping edges →        │
 * │  target fields, with join/aggregation/transform nodes.              │
 * │                                                                     │
 * │  NOT the pipeline/workspace canvas. The pipeline canvas (table-     │
 * │  level DAG) lives in ui/src/components/workspace/DAGCanvas.tsx      │
 * │  and uses a completely separate node type registry, graph builder,  │
 * │  and layout engine.                                                 │
 * │                                                                     │
 * │  See docs/CANVAS_ARCHITECTURE.md for the full distinction.          │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Graph builder: ui/src/canvas/graph/buildCanvasGraph.ts
 * ReactFlow canvas: ui/src/canvas/GraphCanvas.tsx
 * Node types: tableGroup, sourceOriginGroup, sourceField, targetField,
 *             targetHeader, joinNode, aggregationNode, intermediateField, etc.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Backdrop,
  Box,
  Chip,
  Button,
  CircularProgress,
  Link,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { apiService } from '../services/api';
import GraphCanvas from '../canvas/GraphCanvas';
import { MappingTableView } from '../canvas/tableView';
import { PipelineView } from '../canvas/pipelineView';
import CanvasToolbar from '../canvas/CanvasToolbar';
import { buildCanvasGraph } from '../canvas/buildCanvasGraph';
import { normalizeColumnsToArray } from '../canvas/graph/schemaResolution';
import CanvasSheetNavigator, { ALL_SHEETS_ID } from '../canvas/CanvasSheetNavigator';
import { CANVAS_GROUP_BODY_PADDING } from '../canvas/layoutConstants';
import AggregationOverlayLegend from '../canvas/AggregationOverlayLegend';
import MappingOverviewPanel from '../canvas/MappingOverviewPanel';
import { useFieldSelection, makeFieldKey } from '../canvas/state/useFieldSelection';
import {
  useReviewItems,
  type ReviewItem,
  type UnmappedTargetField,
} from '../canvas/hooks/useReviewItems';
import type { CanvasGraph, CanvasSelection, CanvasNode } from '../canvas/types';
import { useProjectContext } from '../context/ProjectContext';
import PreviewPanel from './PreviewPanel';
import DataPreviewModal from './DataPreviewModal';


// Hooks
import {
  useCanvasQueries,
  useCanvasSelection,
  useCanvasSearch,
  useCanvasFocus,
  useCanvasKeyboard,
  useCanvasMutations,
  useTablePreview,
  useAggregationOverlay,
  useGuardrailStatus,
  useLineageComputation,
  derivePreviewTableCandidates,
} from '../canvas/hooks';
import type { TablePreviewTarget, AggregationOverlayNodeConfig } from '../canvas/hooks';
import {
  DeleteMappingDialog,
  TypeMismatchPopover,
  GeneratedSqlPanel,
  JoinBuilderModal,
  AggregationBuilderModal,
} from '../canvas/dialogs';
import CanvasContextMenu from '../canvas/CanvasContextMenu';
import { CANVAS_LAYOUT_DENSITY_KEY } from '../utils/storageKeys';
import { fetchGeneratedSql } from '../canvas/utils/generateSql';
import SchemaDiffBanner from '../canvas/SchemaDiffBanner';
import { useProjectSources } from './workspace/hooks/useProjectSources';
import AddSourceDialog from './workspace/AddSourceDialog';
import * as mappingApi from '../services/mappingApi';
import { getSchema } from '../services/schemaApi';
import type { Schema } from '../services/types';

type StatusMessage = { type: 'success' | 'error' | 'warning'; message: string };

/** Fetch a schema by ID when the project context doesn't provide one. */
function useSchemaOverride(schemaId: string | undefined): Schema | undefined {
  const { data } = useQuery(
    ['schema-override', schemaId],
    () => getSchema(schemaId!),
    { enabled: !!schemaId, staleTime: 60_000 },
  );
  return data ?? undefined;
}

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof (error as any)?.message === 'string') return (error as any).message as string;
  return fallback;
};

// ─── Error Boundary ──────────────────────────────────────────────────────────
class CanvasErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[CanvasErrorBoundary]', error.message, errorInfo.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Rendering Error</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {this.state.error?.message}
            </Typography>
          </Alert>
          {this.state.errorInfo && (
            <Box component="pre" sx={{ fontSize: 11, overflow: 'auto', maxHeight: 400, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {this.state.errorInfo.componentStack}
            </Box>
          )}
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ─── Layout Density ──────────────────────────────────────────────────────────
type LayoutDensity = 'compact' | 'comfortable' | 'spacious';


const LAYOUT_DENSITY_PRESETS: Record<
  LayoutDensity,
  { label: string; description: string; scale: number; groupWidth: number }
> = {
  compact: { label: 'Compact', description: 'Original spacing, suitable for smaller graphs.', scale: 1, groupWidth: 320 },
  comfortable: { label: 'Comfortable', description: 'Adds breathing room between groups to reduce edge overlap.', scale: 1.18, groupWidth: 340 },
  spacious: { label: 'Spacious', description: 'Maximum separation for dense workbooks and review sessions.', scale: 1.32, groupWidth: 360 },
};

const readStoredLayoutDensity = (): LayoutDensity => {
  if (typeof window === 'undefined') return 'comfortable';
  const raw = window.localStorage.getItem(CANVAS_LAYOUT_DENSITY_KEY);
  if (raw === 'compact' || raw === 'comfortable' || raw === 'spacious') return raw;
  return 'comfortable';
};

const hintSeverityToChipColor = (severity?: string): 'default' | 'error' | 'warning' | 'success' | 'info' => {
  switch ((severity ?? '').toLowerCase()) {
    case 'error': return 'error';
    case 'warning': return 'warning';
    case 'success': return 'success';
    case 'info': return 'info';
    default: return 'default';
  }
};

// ─── Props (embedded mode) ───────────────────────────────────────────────────
/** Mutation handlers exposed to parent when embedded. */
export interface EmbeddedMutationHandlers {
  handleSave: (...args: any[]) => void;
  handleCreateMapping: (...args: any[]) => void;
  handleDeleteMapping: (...args: any[]) => void;
  handleValidateTransformation: (...args: any[]) => void;
  handleRegenerateTransformation: (...args: any[]) => void;
  isCreatingMapping: boolean;
  isValidatingMapping: boolean;
  isRegeneratingMapping: boolean;
  isDeletingMapping: boolean;
  isSavingMapping: boolean;
}

interface CanvasMappingEditorProps {
  batchIdProp?: string;
  embedded?: boolean;
  /** Callback to return to agent mode (replaces "← Back to Workspace" navigate). */
  onBack?: () => void;
  onSelectionChange?: (selection: CanvasSelection | null) => void;
  /** Exposes internal mapping data + health + lineage + activity to parent (for right context panel). */
  onDataReady?: (data: import('./workspace/MappingCanvasContent').MappingCanvasDataPayload) => void;
  /** Exposes mutation handlers to parent when embedded (for LogicEditorTab). */
  onMutationHandlers?: (handlers: EmbeddedMutationHandlers) => void;
  /** Session ID for workspace-based preview (rich DataExplorerTab). */
  sessionId?: string;
  /** Workspace `destination_status` when embedded in agent workspace (export lifecycle). */
  destinationExportStatus?: string | null;
  /** Target schema ID from session/batch meta — fallback when project has no target_schema_id. */
  targetSchemaIdOverride?: string | null;
}

// ─── Main Component ──────────────────────────────────────────────────────────
const CanvasMappingEditor: React.FC<CanvasMappingEditorProps> = ({
  batchIdProp,
  embedded,
  onBack: _onBack,
  onSelectionChange: _onSelectionChange,
  onDataReady: _onDataReady,
  onMutationHandlers: _onMutationHandlers,
  sessionId: _sessionId,
  destinationExportStatus,
  targetSchemaIdOverride,
}) => {
  const { batchId: routeBatchId } = useParams<{ batchId: string }>();
  const batchId = batchIdProp ?? routeBatchId;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { selectedProjectId, targetSchema: projectTargetSchema, projectSummary, refreshSummary } = useProjectContext();

  // When the project has no target schema but the session/batch meta does,
  // fetch the schema directly. This happens when the target schema is set
  // during mapping initiation (via session meta) rather than on the project.
  const overrideSchemaId = targetSchemaIdOverride && !projectTargetSchema?.schema_id ? targetSchemaIdOverride : undefined;
  const overrideSchemaQuery = useSchemaOverride(overrideSchemaId);
  const targetSchema = projectTargetSchema ?? overrideSchemaQuery ?? undefined;
  const [toastMessage, setToastMessage] = useState<StatusMessage | null>(null);
  const [inspectorStatus, setInspectorStatus] = useState<StatusMessage | null>(null);
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  const [activeSheetId, setActiveSheetId] = useState<string>(ALL_SHEETS_ID);
  const [showGrid, setShowGrid] = useState(true);
  const [showDataPreview, setShowDataPreview] = useState(true);
  const [graphRevision, setGraphRevision] = useState(0);
  const [layoutDensity, setLayoutDensity] = useState<LayoutDensity>(() => readStoredLayoutDensity());
  const layoutDensityConfig = useMemo(() => LAYOUT_DENSITY_PRESETS[layoutDensity], [layoutDensity]);
  const [canvasViewMode, setCanvasViewMode] = useState<'canvas' | 'mapping-table'>('canvas');
  const [canvasSubState, setCanvasSubState] = useState<'overview' | 'detail'>('overview');
  const [detailTableId, setDetailTableId] = useState<string | null>(null);
  const [detailTableRole, setDetailTableRole] = useState<'source' | 'target' | null>(null);
  const [pipelineSelectedTarget, setPipelineSelectedTarget] = useState<string | null>(null);

  // Recipe-Batch Decoupling state
  const [generationMode, setGenerationMode] = useState<'auto' | 'full'>('auto');
  const [loadingData, setLoadingData] = useState(false);

  // Phase 3D dialog states
  const [sqlPanelOpen, setSqlPanelOpen] = useState(false);
  const [sqlPanelData, setSqlPanelData] = useState<Record<string, string>>({});
  const [sqlPanelLoading, setSqlPanelLoading] = useState(false);
  const [joinBuilderOpen, setJoinBuilderOpen] = useState(false);
  const [aggBuilderOpen, setAggBuilderOpen] = useState(false);
  const [dataPreviewModalOpen, setDataPreviewModalOpen] = useState(false);
  // Issue #656: drift-banner Broken/Stale chips activate the Review-pane tab.
  const [reviewInitialTab, setReviewInitialTab] = useState<
    'all' | 'error' | 'warning' | 'decision' | 'unmapped' | 'broken' | 'stale' | undefined
  >(undefined);
  const handleReviewBroken = useCallback(() => setReviewInitialTab('broken'), []);
  const handleReviewStale = useCallback(() => setReviewInitialTab('stale'), []);

  // Phase 4.8: Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    node: CanvasNode | null;
  } | null>(null);

  // ── Queries ──
  const {
    mappingQuery,
    mappingResponse,
    healthQuery,
    pendingDecisionsQuery,
    canvasGraphQuery,
    rulesQuery,
    computedActiveWorkbookId,
    refetchCanvasGraph,
    isMappingInProgress,
    batchStatus,
  } = useCanvasQueries({ batchId, activeWorkbookId: batchId });
  const activeWorkbookId = batchId ?? computedActiveWorkbookId;

  // Phase 4b: Project sources for multi-source awareness
  const { sources: projectSources } = useProjectSources(selectedProjectId ?? undefined, batchId);

  // Add Source dialog state (triggered by persistent canvas action node)
  const [addSourceDialogOpen, setAddSourceDialogOpen] = useState(false);
  const handleOpenAddSourceDialog = useCallback(() => setAddSourceDialogOpen(true), []);
  const handleCloseAddSourceDialog = useCallback(() => setAddSourceDialogOpen(false), []);
  const handleOpenAddSourceDialogRef = useRef(handleOpenAddSourceDialog);
  handleOpenAddSourceDialogRef.current = handleOpenAddSourceDialog;

  // ── Focus / Expand ──
  const meta = mappingResponse?.metadata as Record<string, unknown> | undefined;
  const srcFilter = meta?.source_tables_filter as string[] | undefined;
  const tgtFilter = meta?.target_tables_filter as string[] | undefined;

  const {
    focusModeEnabled,
    setFocusModeEnabled,
    focusedTargetIds,
    focusedSourceIds,
    expandedGroups,
    setExpandedGroups,
    fitViewTrigger,
    setFitViewTrigger,
    handleToggleFocusTarget,
    handleToggleFocusSource,
    handleClearFocus,
    handleToggleFocusMode,
    handleToggleGroup,
    availableGroups,
  } = useCanvasFocus({ mappingResponse, activeWorkbookId, initialSourceFilter: srcFilter, initialTargetFilter: tgtFilter });

  // Auto-fit on view mode change
  useEffect(() => {
    const timer = setTimeout(() => setFitViewTrigger((n) => n + 1), 200);
    return () => clearTimeout(timer);
  }, [canvasViewMode, setFitViewTrigger]);

  // Reset state on workbook change
  useEffect(() => {
    setActiveSheetId(ALL_SHEETS_ID);
    setCanvasSubState('overview');
    setDetailTableId(null);
    setDetailTableRole(null);
    setPipelineSelectedTarget(null);
    // expandedGroups reset is handled inside useCanvasFocus
  }, [activeWorkbookId]);

  // Phase 0d: Derive active table selection sets from mapping response metadata.
  // These are the subsets the user chose in the Generate Mapping dialog and propagated
  // through the backend. Groups outside the selection are dimmed (not hidden).
  const activeSourceTables = useMemo(() => {
    if (!srcFilter?.length) return undefined;
    return new Set(srcFilter);
  }, [srcFilter]);
  const activeTargetTables = useMemo(() => {
    if (!tgtFilter?.length) return undefined;
    return new Set(tgtFilter);
  }, [tgtFilter]);

  const detailFilter = useMemo(
    () =>
      canvasSubState === 'detail' && detailTableId && detailTableRole
        ? { tableId: detailTableId, role: detailTableRole }
        : null,
    [canvasSubState, detailTableId, detailTableRole],
  );

  // ── Canvas Graph ──
  // focusedTargetIds/focusedSourceIds are needed here because buildCanvasGraph
  // filters mappings BEFORE layout — this determines which groups get created.
  const canvasGraph = useMemo(() => {
    return buildCanvasGraph(
      mappingResponse,
      showGrid,
      layoutDensity,
      expandedGroups,
      focusedTargetIds,
      focusedSourceIds,
      projectSources,
      activeSourceTables,
      activeTargetTables,
      detailFilter,
    );
  }, [mappingResponse, showGrid, layoutDensity, expandedGroups, focusedTargetIds, focusedSourceIds, projectSources, activeSourceTables, activeTargetTables, detailFilter]);

  const canvasGraphFetching = canvasGraphQuery?.isFetching ?? false;
  const canvasGraphError = canvasGraphQuery?.error ?? null;
  const isGraphRefreshing = mappingQuery.isFetching || canvasGraphFetching;

  // ── Selection ──
  const {
    selection,
    setSelection,
    lastStableSelectionRef,
    pendingCreateSelection,
    setPendingCreateSelection,
    handleSelectionChange,
    handleNodeClick,
    handleOverviewSelectEdge,
    clearSelection,
  } = useCanvasSelection({ canvasGraph, isGraphRefreshing });

  // Clear inspector on selection change
  useEffect(() => {
    setInspectorStatus(null);
    setInspectorError(null);
  }, [selection]);

  // ── Issue #656: unified field selection + review-items pipeline ──
  const fieldSelection = useFieldSelection();

  // ── Readiness deep-link receiver ─────────────────────────────────
  // The Readiness deep-link router (used by AssemblyInspectorPanel,
  // ReviewPane banner rows, etc.) constructs URLs like:
  //   /batches/:batchId/mapping
  //     ?focus_table=Subject&focus_field=StudyID
  //     &finding=REQUIRED_FIELD_NOT_MAPPED&action=fix_in_mapping
  // When the canvas mounts (or the URL changes), honor those params
  // by selecting the referenced field through useFieldSelection so the
  // canvas / table view scrolls + highlights it. We consume the param
  // (delete it from the URL) after selection so a subsequent refresh
  // doesn't re-trigger; the user's selection state then drives the UI.
  // Skip silently when params are missing or fieldSelection isn't ready.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    const focusTable = searchParams.get('focus_table');
    const focusField = searchParams.get('focus_field');
    const focusLevel = searchParams.get('focus_level');
    // No focus params in the URL — reset the gate so a *future* click
    // on a Readiness card while the user is already on /mapping
    // re-fires this effect (it wouldn't otherwise: the ref would be
    // stuck at `true` from the previous apply). The clean-up step
    // below clears the params after applying, so this branch is the
    // natural place to reopen the gate.
    if (!focusTable && !focusField && !focusLevel) {
      deepLinkAppliedRef.current = false;
      return;
    }
    if (deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    fieldSelection.selectField(
      focusTable || focusLevel || '',
      focusField || focusTable || focusLevel || '',
      'review-pane',
    );
    // Visible feedback so the button doesn't feel "dead". The receiver
    // fires fieldSelection.selectField which scrolls the matching
    // ReviewItemRow into view when one exists, but for hierarchical-
    // level findings (e.g. GlobalVariables, which is a level not a
    // mapped column) no ReviewItem matches and the user sees no
    // visual change. Surface a toast so the user knows the click
    // landed + which field/table the system is trying to show them.
    const label = focusField
      ? `${focusTable || ''}${focusTable && focusField ? '.' : ''}${focusField}`
      : (focusTable || focusLevel || '');
    // ``adjust_schema`` deep-links land here too (the plan §"One
    // button per issue" notes there's no standalone schema editor
    // route today). Show a different message so the user
    // understands the canvas focus *is* the schema landing — the
    // button isn't broken, just routes through the mapping view.
    const editSchema = searchParams.get('edit_schema') === 'true';
    if (label) {
      setToastMessage({
        type: editSchema ? 'warning' : 'success',
        message: editSchema
          ? `Schema editing isn't available here yet — focusing the field instead: ${label}`
          : `Looking for ${label} in the mapping…`,
      });
    }
    // Clean up the URL so refreshing doesn't re-fire. Preserve the
    // finding/action params as breadcrumb context.
    const next = new URLSearchParams(searchParams);
    next.delete('focus_table');
    next.delete('focus_field');
    next.delete('focus_level');
    next.delete('edit_schema');
    setSearchParams(next, { replace: true });
  }, [searchParams, fieldSelection, setSearchParams]);

  // Derive unmapped target fields from schema snapshot minus mapped set.
  const unmappedTargetFields = useMemo<UnmappedTargetField[]>(() => {
    const meta = mappingResponse?.metadata as Record<string, any> | undefined;
    const snapshot =
      meta?.target_schema_snapshot?.tables ??
      meta?.schema_context?.target_schema_snapshot?.tables ??
      mappingResponse?.transformation_plan?.target_schema_snapshot?.tables;
    if (!snapshot || typeof snapshot !== 'object') return [];
    const mapped = new Set(
      (mappingResponse?.mappings ?? []).map(
        (m) => `${m.target_table}.${m.target_field}`.toLowerCase(),
      ),
    );
    const out: UnmappedTargetField[] = [];
    for (const [tableName, tableDef] of Object.entries(
      snapshot as Record<string, { columns?: Array<{ name: string; required?: boolean; nullable?: boolean }> }>,
    )) {
      for (const col of normalizeColumnsToArray(tableDef.columns)) {
        if (!col?.name) continue;
        const k = `${tableName}.${col.name}`.toLowerCase();
        if (mapped.has(k)) continue;
        out.push({
          target_table: tableName,
          target_field: col.name,
          is_required: col.required === true || col.nullable === false,
        });
      }
    }
    return out;
  }, [mappingResponse]);

  const reviewBundle = useReviewItems({
    mappingResponse: mappingResponse ?? null,
    healthIssues: healthQuery.data?.issues,
    pendingDecisions: pendingDecisionsQuery?.data?.items,
    unmappedTargetFields,
  });

  // Adapt canvas selection (node/edge) → unified field selection so the
  // Review pane and field-scoped Inspector re-scope together.
  useEffect(() => {
    if (!selection) {
      fieldSelection.clear();
      return;
    }
    if (selection.kind === 'node') {
      const d = selection.node.data;
      if (d?.kind === 'targetField') {
        fieldSelection.selectField(
          d.table,
          d.field,
          'canvas',
          (d.mapping?.hierarchy_path ?? undefined) as Record<string, string> | undefined,
        );
        return;
      }
      if (d?.kind === 'tableGroup' && d.groupRole === 'target') {
        fieldSelection.selectTable(d.table, 'canvas');
        return;
      }
      fieldSelection.clear();
      return;
    }
    if (selection.kind === 'edge') {
      const mappingId = selection.edge.data?.mappingId;
      const m = (mappingResponse?.mappings ?? []).find((x) => x.mapping_id === mappingId);
      if (m) {
        fieldSelection.selectField(
          m.target_table,
          m.target_field,
          'canvas',
          (m.hierarchy_path ?? undefined) as Record<string, string> | undefined,
        );
      } else {
        fieldSelection.clear();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, mappingResponse]);

  // Issue #656: derive field-scoped data for the PreviewPanel "Field
  // Detail" tab. Reads from already-fetched payloads — no new
  // queries.
  const fieldScoped = useMemo(() => {
    const sel = fieldSelection.selection;
    if (!sel) {
      return {
        fieldReviewItems: undefined as ReviewItem[] | undefined,
        fieldValidatorRules: undefined,
        fieldValidatorOverrides: undefined,
        fieldIsPrimaryKey: undefined,
        overriddenRuleNames: undefined as Set<string> | undefined,
      };
    }
    // Plan §G/10: when scope is `'table'`, surface table-scoped review
    // items only (no rules/samples).
    if (sel.scope === 'table') {
      const allForTable = reviewBundle.items.filter(
        (it) => it.target_table === sel.target_table,
      );
      return {
        fieldReviewItems: allForTable,
        fieldValidatorRules: undefined,
        fieldValidatorOverrides: undefined,
        fieldIsPrimaryKey: undefined,
        overriddenRuleNames: undefined as Set<string> | undefined,
      };
    }
    // Items: pre-grouped by `useReviewItems` keyed on `makeFieldKey`.
    const items = reviewBundle.byField.get(
      makeFieldKey(sel.target_table, sel.target_field, sel.hierarchy_path),
    );
    // Validator config + overrides best-effort from recipe metadata.
    const meta = mappingResponse?.metadata as Record<string, any> | undefined;
    const tableSnapshot =
      meta?.target_schema_snapshot?.tables?.[sel.target_table] ??
      meta?.schema_context?.target_schema_snapshot?.tables?.[sel.target_table] ??
      mappingResponse?.transformation_plan?.target_schema_snapshot?.tables?.[sel.target_table];
    const column = normalizeColumnsToArray(tableSnapshot?.columns).find(
      (c) => c.name === sel.target_field,
    );
    const rules = (column?.validator_rules ?? column?.rules ?? undefined) as
      | Array<{ rule?: string; type?: string; [k: string]: unknown }>
      | undefined;
    // Refinement (Apr 2026): `validator_overrides` is now a typed
    // first-class field on `MappingResponse`, not smuggled through
    // `metadata`. Read from the typed location directly.
    const overrides =
      mappingResponse?.validator_overrides?.[sel.target_table]?.[sel.target_field] ??
      undefined;
    const overriddenRuleNames = overrides
      ? new Set(overrides.map((o) => o.rule))
      : undefined;
    return {
      fieldReviewItems: items,
      fieldValidatorRules: rules,
      fieldValidatorOverrides: overrides,
      fieldIsPrimaryKey: Boolean(column?.is_primary_key ?? column?.primary_key),
      overriddenRuleNames,
    };
  }, [fieldSelection.selection, reviewBundle.byField, mappingResponse]);

  // Plan §G/2: when a field is selected and `sample_output` is empty
  // for its table, kick `getDistributedPreview` once. First-time
  // materialization, not a fallback.
  const [materializing, setMaterializing] = useState(false);
  const handleMaterializeSamples = useCallback(async () => {
    if (!batchId || !mappingResponse?.recipe_id) return;
    setMaterializing(true);
    try {
      await apiService.getDistributedPreview(batchId, mappingResponse.recipe_id);
      queryClient.invalidateQueries(['mappings', batchId]);
    } catch (err) {
      // Surface failure quietly — user can retry via the explicit button.
      // eslint-disable-next-line no-console
      console.warn('Sample materialization failed', err);
    } finally {
      setMaterializing(false);
    }
  }, [batchId, mappingResponse?.recipe_id, queryClient]);


  // Forward selection to parent (workspace MappingContextPanel)
  useEffect(() => {
    _onSelectionChange?.(selection);
  }, [selection, _onSelectionChange]);

  // ── Search ──
  const {
    canvasSearchQuery,
    setCanvasSearchQuery,
    canvasFilter,
    setCanvasFilter,
    canvasSearchMatchIndex,
    canvasSearchResults,
    canvasHighlightNodeIds,
    focusNodeId,
    setFocusNodeId,
    handleSearchNavigate,
  } = useCanvasSearch({ canvasGraph });

  // Plan §G/9: open inline edit for an unmapped target field. Switches
  // to mapping-table view (so the row is reachable) and selects the
  // field via `useFieldSelection` — `MappingTableView` will scroll the
  // row into view via its `data-row-id` effect, and the table-row
  // `inlineEditOpen` toggles via the existing canvas selection.
  const handleSelectInlineEdit = useCallback(
    (target_table: string, target_field: string) => {
      setCanvasViewMode('mapping-table');
      fieldSelection.selectField(target_table, target_field, 'review-pane');
    },
    [fieldSelection],
  );

  // Click in Review pane → set canvas selection + camera focus + pan.
  // Placed after `useCanvasSearch` so `setFocusNodeId` is in scope.
  const handleReviewItemSelect = useCallback(
    (item: ReviewItem) => {
      if (item.target_field) {
        fieldSelection.selectField(
          item.target_table,
          item.target_field,
          'review-pane',
          item.hierarchy_path,
        );
        const node = canvasGraph?.nodes.find(
          (n) =>
            n.data.kind === 'targetField' &&
            n.data.table === item.target_table &&
            n.data.field === item.target_field,
        );
        if (node) {
          setSelection({ kind: 'node', node });
          setFocusNodeId(node.id);
          setFitViewTrigger((n) => n + 1);
        } else {
          const groupNode = canvasGraph?.nodes.find(
            (n) =>
              (n.data.kind === 'tableGroup' || n.data.kind === 'tableNode') &&
              n.data.table === item.target_table,
          );
          if (groupNode) setFocusNodeId(groupNode.id);
        }
      } else {
        fieldSelection.selectTable(item.target_table, 'review-pane');
        const groupNode = canvasGraph?.nodes.find(
          (n) =>
            (n.data.kind === 'tableGroup' || n.data.kind === 'tableNode') &&
            n.data.table === item.target_table,
        );
        if (groupNode) setFocusNodeId(groupNode.id);
      }
    },
    [canvasGraph, fieldSelection, setSelection, setFocusNodeId, setFitViewTrigger],
  );
  const prevUrlFieldIdRef = useRef<string | null>(null);
  const prevSelectionIdRef = useRef<string | null>(null);

  // Handle Bidirectional Deep Linking (?field=<id> <-> selection)
  useEffect(() => {
    const urlFieldId = searchParams.get('field');
    const currentSelectionId = selection?.kind === 'node' ? selection.node.id : selection?.kind === 'edge' ? selection.edge.id : null;
    const isGraphLoaded = canvasGraph.nodes.length > 0;

    const urlChanged = urlFieldId !== prevUrlFieldIdRef.current;
    const selectionChanged = currentSelectionId !== prevSelectionIdRef.current;

    if (!isGraphLoaded || (!urlChanged && !selectionChanged)) {
      return;
    }

    if (urlChanged && !selectionChanged) {
      // URL changed -> Sync to State
      if (urlFieldId) {
        const node = canvasGraph.nodes.find(n => n.id === urlFieldId || n.data?.label === urlFieldId);
        if (node) {
          handleSelectionChange({ kind: 'node', node });
          setFocusNodeId(node.id);
        } else if (urlFieldId.startsWith('targetField:') || urlFieldId.startsWith('sourceField:')) {
          // Auto-expand parent group if node not found
          const parts = urlFieldId.split(':');
          if (parts.length === 2) {
            const kind = parts[0];
            const rest = parts[1];
            const tableName = rest.split('.')[0];
            const groupType = kind === 'targetField' ? 'target' : 'source';
            const groupId = `group:${groupType}:${tableName}`;

            // Determine potential origin group ID (for source tables)
            const originKey = groupType === 'source' && tableName.includes('::')
              ? tableName.split('::')[0]
              : '__file_uploads__';
            const originId = `origin:${originKey}`;

            if (!expandedGroups.has(groupId) || (groupType === 'source' && !expandedGroups.has(originId))) {
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                next.add(groupId);
                if (groupType === 'source') {
                  next.add(originId);
                }
                return next;
              });
              // Wait for re-render with expanded group(s)
              return;
            }
          }
          handleSelectionChange(null);
        } else {
          const edge = canvasGraph.edges.find(e => e.id === urlFieldId || e.data?.label === urlFieldId);
          if (edge) {
            handleSelectionChange({ kind: 'edge', edge });
            setFocusNodeId(edge.id);
          } else {
            handleSelectionChange(null);
          }
        }
      } else {
        handleSelectionChange(null);
      }
      prevUrlFieldIdRef.current = urlFieldId;
    }
    else if (selectionChanged && !urlChanged) {
      // State changed -> Sync to URL
      if (urlFieldId !== currentSelectionId) {
        if (currentSelectionId) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('field', currentSelectionId);
              return next;
            },
            { replace: true }
          );
          prevUrlFieldIdRef.current = currentSelectionId;
        } else {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('field');
              return next;
            },
            { replace: true }
          );
          prevUrlFieldIdRef.current = null;
        }
      }
      prevSelectionIdRef.current = currentSelectionId;
    } 
    else {
      // Either both changed or neither changed
      prevUrlFieldIdRef.current = urlFieldId;
      prevSelectionIdRef.current = currentSelectionId;
    }
  }, [searchParams, canvasGraph, selection, handleSelectionChange, setSearchParams, expandedGroups, setExpandedGroups, setFocusNodeId]);

  // ── Keyboard Shortcuts ──
  const allGroupIds = useMemo(
    () => availableGroups.map(g => `group:${g.role}:${g.id}`),
    [availableGroups]
  );
  useCanvasKeyboard({
    selectedNodeId: selection?.kind === 'node' ? selection.node.id : null,
    expandedGroups,
    onToggleGroup: handleToggleGroup,
    onSetExpandedGroups: setExpandedGroups,
    allGroupIds,
  });

  // ── Guardrails ──
  const guardrailStatus = useGuardrailStatus({
    mappingResponse,
    selectedProjectId,
    schemaId: targetSchema?.schema_id,
  });

  // ── Table Preview ──
  const recordCanvasEvent = useCallback(
    (kind: string, payload: Record<string, string>) => {
      if (!activeWorkbookId) return;
      void apiService
        .recordCanvasEvent(activeWorkbookId, { kind, actor: 'web-ui', payload })
        .catch((eventError) => console.warn('Failed to record canvas event', eventError));
    },
    [activeWorkbookId],
  );

  const {
    tablePreviewOpen,
    tablePreviewTarget,
    tablePreviewLoading,
    tablePreviewError,
    tablePreviewNotice,
    tablePreviewResponse,
    tablePreviewSampleOutput,
    tablePreviewHasSampleRows,
    handleOpenTablePreview,
    handleCloseTablePreview,
  } = useTablePreview({
    mappingResponse,
    activeWorkbookId,
    recordCanvasEvent,
    onPreviewComplete: useCallback(() => {
      // After preview completes, expression failures may have been recorded —
      // invalidate health query so badges + alerts update
      queryClient.invalidateQueries(['mapping-health', batchId]);
    }, [queryClient, batchId]),
  });

  // ── Mutations ──
  const {
    generateMappingsMutation,
    approveMappingsMutation,
    updateMappingMutation,
    deleteMappingMutation,
    applyRuleMutation,
    deleteMappingDialog,
    setDeleteMappingDialog,
    undoingAdjustmentId,
    redoingAdjustmentId,
    isRegeneratingMapping,
    isValidatingMapping,
    isCreatingMapping,
    handleDeleteMapping,
    handleConfirmDeleteMapping,
    handleCreateMapping,
    handleCanvasConnect,
    handleRegenerateTransformation,
    handleValidateTransformation,
    handleSave,
    handleApplyRule,
    handleGenerateDraft,
    handleApproveMapping,
    handleSaveSchemaEnrichment,
    handleUndoAdjustment,
    handleRedoAdjustment,
    handleFeedbackSuccess: _handleFeedbackSuccess,
    handleRefresh,
    updateCanvasGraphCache,
    // Phase 4.7: Type mismatch
    typeMismatch,
    setTypeMismatch,
    handleTypeMismatchApplyCast,
    handleTypeMismatchProceed,
  } = useCanvasMutations({
    batchId,
    activeWorkbookId,
    mappingResponse,
    canvasGraph,
    guardrailStatus,
    selectedProjectId,
    schemaId: targetSchema?.schema_id,
    generationMode,
    onToast: setToastMessage,
    onInspectorStatus: setInspectorStatus,
    onInspectorError: setInspectorError,
    recordCanvasEvent,
    clearSelection,
    setPendingCreateSelection,
    refetchCanvasGraph,
  });

  // ── Load Data (post-approval) ──
  const handleLoadData = useCallback(async () => {
    if (!mappingResponse?.recipe_id || !batchId) {
      setToastMessage({ type: 'error', message: 'Recipe or batch not available.' });
      return;
    }
    setLoadingData(true);
    try {
      const result = await mappingApi.loadRecipeData(mappingResponse.recipe_id, batchId);
      setToastMessage({
        type: result.status === 'completed' ? 'success' : 'warning',
        message: `Load ${result.status}: ${result.rows_processed ?? 0} rows across ${result.table_results?.length ?? 0} tables`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load data';
      setToastMessage({ type: 'error', message: msg });
    } finally {
      setLoadingData(false);
    }
  }, [mappingResponse?.recipe_id, batchId]);


  // ── Aggregation Overlay ──
  const {
    aggregationOverlayEnabled,
    setAggregationOverlayEnabled,
    focusedAggregationId,
    aggregationOverlayAvailable,
    aggregationOverlayActive,
    aggregationOverlayConfigMap,
    aggregationOverlayLegendSteps,
    aggregationTelemetrySummary,
    handleFocusAggregation,
    handleToggleAggregationOverlay,
  } = useAggregationOverlay({
    canvasGraph,
    mappingResponse,
    activeWorkbookId,
    recordCanvasEvent,
  });

  // Reset aggregation overlay on workbook change
  useEffect(() => {
    setAggregationOverlayEnabled(false);
  }, [activeWorkbookId, setAggregationOverlayEnabled]);

  // ── Lineage ──
  const { computedLineageChains, computedLineage } = useLineageComputation({
    selection,
    mappingResponse,
  });

  // ── Derived Data ──
  const workbookMetadata = mappingResponse?.metadata?.workbook;
  const transformationRules = rulesQuery.data?.rules ?? [];
  const aggregationPlan = mappingResponse?.metadata?.aggregation_plan ?? [];
  const promptRuns = canvasGraphQuery.data?.metadata?.prompt_runs ?? [];
  const manualAdjustments = canvasGraphQuery.data?.metadata?.manual_adjustments ?? [];

  const edgeFieldMap = useMemo<Record<string, string>>(() => {
    const graphData = canvasGraphQuery.data;
    if (!graphData) return {};
    const nodeLabels: Record<string, string> = {};
    for (const n of graphData.nodes) nodeLabels[n.id] = n.label;
    const map: Record<string, string> = {};
    for (const e of graphData.edges) {
      const targetLabel = nodeLabels[e.target] || '';
      const sourceLabel = nodeLabels[e.source] || '';
      map[e.id] = [sourceLabel, targetLabel].filter(Boolean).join(' → ');
    }
    return map;
  }, [canvasGraphQuery.data]);

  // Forward mapping data to parent (workspace MappingContextPanel)
  useEffect(() => {
    _onDataReady?.({
      mappingResponse,
      healthData: healthQuery.data,
      healthLoading: healthQuery.isLoading,
      lineageChains: computedLineageChains,
      manualAdjustments,
      promptRuns,
      edgeFieldMap,
    });
  }, [mappingResponse, healthQuery.data, healthQuery.isLoading, computedLineageChains, manualAdjustments, promptRuns, edgeFieldMap, _onDataReady]);

  // Forward mutation handlers to parent (workspace LogicEditorTab)
  useEffect(() => {
    _onMutationHandlers?.({
      handleSave,
      handleCreateMapping,
      handleDeleteMapping,
      handleValidateTransformation,
      handleRegenerateTransformation,
      isCreatingMapping,
      isValidatingMapping,
      isRegeneratingMapping,
      isDeletingMapping: deleteMappingMutation.isLoading,
      isSavingMapping: updateMappingMutation.isLoading || isCreatingMapping,
    });
  }, [
    handleSave, handleCreateMapping, handleDeleteMapping,
    handleValidateTransformation, handleRegenerateTransformation,
    isCreatingMapping, isValidatingMapping, isRegeneratingMapping,
    deleteMappingMutation.isLoading, updateMappingMutation.isLoading,
    _onMutationHandlers,
  ]);

  const edgeProvenanceByMapping = useMemo(() => {
    if (!canvasGraph) return {};
    const result: Record<string, any[]> = {};
    canvasGraph.edges.forEach((edge) => {
      const mappingId = edge.data?.mappingId;
      if (!mappingId) return;
      const entry = {
        id: edge.id,
        label: edge.data?.label,
        kind: edge.data?.kind,
        confidence: edge.data?.confidence,
        aiGenerated: edge.data?.aiGenerated,
        provenance: edge.data?.provenance,
        metadata: edge.data?.metadata,
        status: edge.data?.status,
      };
      if (!result[mappingId]) result[mappingId] = [];
      result[mappingId].push(entry);
    });
    Object.values(result).forEach((entries) => {
      entries.sort((l, r) => {
        const ll = l.label ?? l.kind ?? l.id;
        const rl = r.label ?? r.kind ?? r.id;
        return ll.localeCompare(rl);
      });
    });
    return result;
  }, [canvasGraph]);

  // ── Layout Density ──
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CANVAS_LAYOUT_DENSITY_KEY, layoutDensity);
    }
  }, [layoutDensity]);


  // ── Overview Panel Handlers ──
  const handleOverviewFocusNode = useCallback((nodeId: string) => {
    setFocusNodeId(nodeId);
  }, [setFocusNodeId]);

  const handleOverviewSelectEdgeWrapper = useCallback(
    (edgeId: string) => {
      handleOverviewSelectEdge(edgeId, setFocusNodeId);
    },
    [handleOverviewSelectEdge, setFocusNodeId],
  );

  const handleSheetSelect = useCallback((sheetId: string) => {
    setActiveSheetId(sheetId);
    clearSelection();
  }, [clearSelection]);

  // ── Drill-in / Drill-out handlers (overview ↔ detail) ──
  const handleDrillIntoTable = useCallback((tableId: string, role: 'source' | 'target') => {
    setDetailTableId(tableId);
    setDetailTableRole(role);
    setCanvasSubState('detail');
    // Expand drilled table only — do not toggle focus filters (breaks zero-mapping batches).
    const groupId = role === 'target' ? `group:target:${tableId}` : `group:source:${tableId}`;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
    setTimeout(() => setFitViewTrigger((n) => n + 1), 200);
  }, [setExpandedGroups, setFitViewTrigger]);

  const handleReturnToOverview = useCallback(() => {
    setCanvasSubState('overview');
    setDetailTableId(null);
    setDetailTableRole(null);
    handleClearFocus();
  }, [handleClearFocus]);

  // Escape key to return to overview
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canvasSubState === 'detail') {
        handleReturnToOverview();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canvasSubState, handleReturnToOverview]);

  // ── Phase 4.8: Context menu handler ──
  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: CanvasNode) => {
      event.preventDefault();
      setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, node });
    },
    [],
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  // ── Phase 4.2: Show SQL handler ──
  const handleShowSql = useCallback(async () => {
    if (!mappingResponse?.recipe_id) return;
    setSqlPanelLoading(true);
    setSqlPanelOpen(true);
    try {
      const sql = await fetchGeneratedSql(mappingResponse.recipe_id);
      setSqlPanelData(sql);
    } catch {
      setSqlPanelData({});
    } finally {
      setSqlPanelLoading(false);
    }
  }, [mappingResponse?.recipe_id]);

  // ── Fix action handler for Health tab ──
  const handleApplyFix = useCallback((issue: import('../services/api').HealthIssue, fixType: string) => {
    if (!mappingResponse?.recipe_id) return;

    // For "create_mapping" fixes, delegate to existing create handler
    if (fixType === 'create_mapping') {
      handleCreateMapping(issue.target_table || '', issue.target_field || '');
      return;
    }

    // For expression-wrapping fixes, find the mapping and update its expression
    const mapping = (mappingResponse.mappings || []).find(
      (m) => m.target_table === issue.target_table && m.target_field === issue.target_field
    );
    if (!mapping) {
      setToastMessage({ type: 'error', message: `No mapping found for ${issue.target_table}.${issue.target_field}` });
      return;
    }

    const currentExpr = mapping.transformation || mapping.source_field || '';
    let newExpr = currentExpr;
    const targetType = (issue.message.match(/target\s+(\S+)/i) || [])[1] || 'VARCHAR';

    switch (fixType) {
      case 'cast':
        newExpr = `CAST(${currentExpr} AS ${targetType})`;
        break;
      case 'coalesce':
        newExpr = `COALESCE(${currentExpr}, '')`;
        break;
      case 'substring': {
        const lenMatch = issue.message.match(/(\d+)/);
        const maxLen = lenMatch ? lenMatch[1] : '255';
        newExpr = `SUBSTRING(${currentExpr}, 1, ${maxLen})`;
        break;
      }
      case 'round':
        newExpr = `ROUND(${currentExpr}, 2)`;
        break;
      default:
        setToastMessage({ type: 'error', message: `Fix type "${fixType}" is not yet automated` });
        return;
    }

    updateMappingMutation.mutate({
      mappingId: mapping.mapping_id,
      payload: {
        recipe_id: mappingResponse.recipe_id,
        transformation: newExpr,
        transformation_type: 'expression',
        edited_by: 'web-ui',
      },
    });
    setToastMessage({ type: 'success', message: `Applied ${fixType}: ${newExpr}` });
  }, [mappingResponse, handleCreateMapping, updateMappingMutation, setToastMessage]);

  // ── Aggregation Overlay Legend ──
  const aggregationOverlayLegendComponent = useMemo(() => {
    if (!aggregationOverlayActive) return undefined;
    return (
      <AggregationOverlayLegend
        steps={aggregationOverlayLegendSteps}
        focusedAggregationId={focusedAggregationId}
        onFocus={handleFocusAggregation}
        summary={aggregationTelemetrySummary}
      />
    );
  }, [aggregationOverlayActive, aggregationOverlayLegendSteps, focusedAggregationId, handleFocusAggregation, aggregationTelemetrySummary]);

  // ── Stable callback refs for displayedGraph (avoid memo invalidation on every render) ──
  const handleToggleGroupRef = useRef(handleToggleGroup);
  handleToggleGroupRef.current = handleToggleGroup;
  const handleReturnToOverviewRef = useRef(handleReturnToOverview);
  handleReturnToOverviewRef.current = handleReturnToOverview;
  const handleOpenTablePreviewRef = useRef(handleOpenTablePreview);
  handleOpenTablePreviewRef.current = handleOpenTablePreview;
  const setSelectionRef = useRef(setSelection);
  setSelectionRef.current = setSelection;
  const setContextMenuRef = useRef(setContextMenu);
  setContextMenuRef.current = setContextMenu;

  // ── Auto-fetch preview on selection ──
  // Depend only on selectionNodeId so refresh (which recreates handleOpenTablePreview) does not
  // retrigger this effect and reopen the Inspector.
  const selectionRefForPreview = useRef(selection);
  selectionRefForPreview.current = selection;
  const selectionNodeId = selection?.kind === 'node' ? selection.node.id : null;
  useEffect(() => {
    const sel = selectionRefForPreview.current;
    if (!sel || sel.kind !== 'node') return;
    const node = sel.node;
    if (!['sourceField', 'targetField', 'tableGroup', 'intermediateField'].includes(node.data.kind)) return;
    if (node.data.isAddSourceAction) return; // Skip "Add Source" action node
    const tableKey = node.data.table || node.data.qualifiedName;
    const role = node.data.groupRole;
    if (!tableKey) return;
    const timer = setTimeout(() => {
      handleOpenTablePreviewRef.current({
        label: node.data.label || node.data.table || 'Table',
        tableKey,
        role,
        sheetId: node.data.sheetId,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [selectionNodeId]);

  // ── Displayed Graph (applies density scaling, sheet filtering, overlay) ──
  const displayedGraph: CanvasGraph = useMemo(() => {
    // Skip expensive graph processing when table view is active (GraphCanvas not rendered)
    if (canvasViewMode === 'mapping-table') return { nodes: [], edges: [] };
    if (!canvasGraph) return { nodes: [], edges: [] };

    const nodes = canvasGraph.nodes.map((node) => {
      const isTableGroup = node.data?.kind === 'tableGroup';
      let sampleRows = node.data?.sampleRows;

      // Handle persistent "Add Source" action node
      if (isTableGroup && node.data?.isAddSourceAction) {
        return {
          ...node,
          data: {
            ...node.data,
            sampleRows,
            showDataPreview: false,
            onToggleExpand: () => handleOpenAddSourceDialogRef.current(),
          },
          style: { ...node.style, pointerEvents: 'auto' as const },
        };
      }

      if (isTableGroup) {
        const tableQualified = node.data?.qualifiedName;
        if (tableQualified) {
          const normalizedSearchKey = tableQualified.toLowerCase().trim();
          const previewKeys = Object.keys(tablePreviewSampleOutput);

          let match = previewKeys.find((k) => k.toLowerCase().trim() === normalizedSearchKey);
          if (!match) {
            const simpleName = normalizedSearchKey.split('.').pop()?.toLowerCase();
            if (simpleName) {
              match = previewKeys.find((k) => {
                const nk = k.toLowerCase().trim();
                return nk === simpleName || nk.endsWith(`.${simpleName}`);
              });
            }
          }
          if (!match && node.data?.groupRole === 'source') {
            const label = node.data.label?.toLowerCase().trim();
            const original = node.data.metadata?.originalLabel as string | undefined;
            if (label) match = previewKeys.find((k) => k.toLowerCase().trim() === label);
            if (!match && original)
              match = previewKeys.find((k) => k.toLowerCase().trim() === original.toLowerCase().trim());
          }
          if (match) sampleRows = tablePreviewSampleOutput[match];
        }
      }

      if (isTableGroup) {
        const isExpanded = expandedGroups.has(node.id);
        return {
          ...node,
          data: {
            ...node.data,
            sampleRows,
            expanded: isExpanded,
            groupExpanded: isExpanded,
            onToggleExpand: () => handleToggleGroupRef.current(node.id),
            showDataPreview: !!node.data?.showDataPreview,
            onDrillOut: canvasSubState === 'detail' ? () => handleReturnToOverviewRef.current() : undefined,
            onOpenContextMenu: (event: React.MouseEvent) => {
              event.preventDefault();
              setContextMenuRef.current({ mouseX: event.clientX, mouseY: event.clientY, node: node as CanvasNode });
            },
          },
        };
      }

      const isOriginGroup = node.data?.kind === 'sourceOriginGroup';
      if (isOriginGroup) {
        return {
          ...node,
          data: {
            ...node.data,
            sampleRows,
            showDataPreview: false,
            onToggleOriginExpand: () => handleToggleGroupRef.current(node.id),
            onAddTableFromOrigin: () => handleOpenAddSourceDialogRef.current(),
          },
          style: node.style ? { ...node.style } : undefined,
          draggable: true,
        };
      }

      return {
        ...node,
        data: { ...node.data, sampleRows, showDataPreview: !!showDataPreview },
        style: node.style ? { ...node.style } : undefined,
        draggable: true,
      };
    });

    const edges = canvasGraph.edges.map((edge) => ({
      ...edge,
      data: edge.data
        ? {
          ...edge.data,
          onIconClick: (edgeId: string) => {
            const targetEdge = canvasGraph.edges.find((e) => e.id === edgeId);
            if (targetEdge) {
              setSelectionRef.current({ kind: 'edge', edge: targetEdge });
              lastStableSelectionRef.current = { kind: 'edge', edge: targetEdge };
            }
          },
        }
        : undefined,
      style: edge.style ? { ...edge.style } : undefined,
    }));

    // Apply density scaling
    const layoutScale = layoutDensityConfig.scale;
    const layoutGroupWidth = layoutDensityConfig.groupWidth;
    const computedFieldWidth = Math.max(layoutGroupWidth - CANVAS_GROUP_BODY_PADDING * 2, 160);

    nodes.forEach((node, index) => {
      const basePosition = node.position ?? { x: 0, y: 0 };
      const scaledPosition = { ...basePosition, y: basePosition.y * layoutScale };
      const baseStyle = node.style ? { ...node.style } : undefined;
      if (baseStyle && typeof baseStyle.height === 'number') {
        baseStyle.height = baseStyle.height * layoutScale;
      }

      if (node.type === 'tableGroup') {
        const nextStyle = { ...(baseStyle ?? {}), width: layoutGroupWidth };
        const displayLabel =
          (node.data?.label?.trim()?.length ? node.data.label.trim() : undefined) ??
          node.data?.qualifiedName ??
          node.data?.table ??
          'Untitled Group';
        const hasSampleRows = node.data?.sampleRows && (node.data.sampleRows as any[]).length > 0;
        const showPreview = node.data?.showDataPreview !== false;
        if (hasSampleRows && showPreview && typeof nextStyle.height === 'number') {
          nextStyle.height += 140 * layoutScale;
        }
        const candidateValues = Array.from(
          new Set(
            [node.data?.qualifiedName, node.data?.table, node.data?.label].filter(
              (v): v is string => Boolean(v && v.trim().length > 0),
            ),
          ),
        );
        const previewTarget: TablePreviewTarget = {
          tableKey: candidateValues[0],
          label: displayLabel,
          role: node.data?.groupRole,
          sheetId: node.data?.sheetId,
          candidates: candidateValues,
        };
        nodes[index] = {
          ...node,
          position: scaledPosition,
          style: nextStyle,
          data: {
            ...node.data,
            previewActions: { openGroupPreview: () => handleOpenTablePreviewRef.current(previewTarget) },
          },
          draggable: !!node.draggable,
        };
        return;
      }

      if ((node.type === 'sourceField' || node.type === 'targetField') && node.data?.groupRole) {
        nodes[index] = {
          ...node,
          position: scaledPosition,
          style: { ...(baseStyle ?? {}), width: computedFieldWidth },
          draggable: !!node.draggable,
        };
        return;
      }

      nodes[index] = { ...node, position: scaledPosition, style: baseStyle } as typeof node;
    });

    // Sheet filtering
    if (workbookMetadata && activeSheetId !== ALL_SHEETS_ID) {
      const visibleNodeIds = new Set<string>();
      nodes.forEach((node) => {
        if (node.data?.sheetId === activeSheetId) visibleNodeIds.add(node.id);
        if (node.type === 'tableGroup' && node.data?.sheetId === activeSheetId) visibleNodeIds.add(node.id);
        if (
          node.data?.kind === 'aggregation' &&
          node.data?.aggregation?.inputRanges?.some((r: any) => r.sheetId === activeSheetId)
        )
          visibleNodeIds.add(node.id);
      });
      if (visibleNodeIds.size > 0) {
        nodes.forEach((node, i) => {
          const vis = visibleNodeIds.has(node.id);
          const bs = node.style ? { ...node.style } : {};
          nodes[i] = { ...node, hidden: !vis, style: vis ? bs : { ...bs, opacity: 0.15 } } as typeof node;
        });
        edges.forEach((edge, i) => {
          const vis = visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target);
          const bs = edge.style ? { ...edge.style } : {};
          edges[i] = { ...edge, hidden: !vis, style: vis ? bs : { ...bs, opacity: 0.1 } };
        });
      }
    }

    // Aggregation overlay highlighting
    if (aggregationOverlayActive) {
      const normaliseAggId = (id: string) => (id || '').replace(/^aggregation:/, '');
      const allowedIds =
        focusedAggregationId && aggregationOverlayConfigMap.has(focusedAggregationId)
          ? new Set<string>([focusedAggregationId])
          : new Set<string>(Array.from(aggregationOverlayConfigMap.keys()));
      const overlayColorByNodeId = new Map<string, AggregationOverlayNodeConfig>();
      const highlightedNodeIds = new Set<string>();
      const highlightedEdgeIds = new Set<string>();

      nodes.forEach((node, i) => {
        if (node.data?.kind !== 'aggregation') return;
        const normalized = normaliseAggId(node.id);
        const config = aggregationOverlayConfigMap.get(normalized);
        if (!config) return;
        const isFocused = allowedIds.has(normalized);
        overlayColorByNodeId.set(node.id, config);
        nodes[i] = {
          ...node,
          data: {
            ...node.data,
            showDataPreview: !!node.data?.showDataPreview,
            overlay: {
              aggregationId: config.aggregationId,
              color: config.color,
              order: config.displayOrder,
              badgeLabel: config.badgeLabel,
              validatorStatus: config.validatorStatus,
              cacheKey: config.cacheKey,
              sheetId: config.sheetId,
              focused: isFocused,
            },
          },
          draggable: !!node.draggable,
          style: node.style ? { ...node.style } : {},
        };
        if (isFocused) highlightedNodeIds.add(node.id);
      });

      if (highlightedNodeIds.size === 0) {
        overlayColorByNodeId.forEach((_, nid) => highlightedNodeIds.add(nid));
      }

      edges.forEach((edge) => {
        const sn = normaliseAggId(edge.source);
        const tn = normaliseAggId(edge.target);
        const sm = overlayColorByNodeId.has(edge.source) || allowedIds.has(sn);
        const tm = overlayColorByNodeId.has(edge.target) || allowedIds.has(tn);
        const ae = edge.data?.kind === 'aggregation';
        if (!sm && !tm && !ae) return;
        const cf =
          highlightedNodeIds.has(edge.source) ||
          highlightedNodeIds.has(edge.target) ||
          allowedIds.has(sn) ||
          allowedIds.has(tn);
        if (!cf) return;
        highlightedEdgeIds.add(edge.id);
        highlightedNodeIds.add(edge.source);
        highlightedNodeIds.add(edge.target);
      });

      nodes.forEach((node, i) => {
        if (node.hidden) return;
        const bs = node.style ? { ...node.style } : {};
        if (highlightedNodeIds.has(node.id)) {
          const config = overlayColorByNodeId.get(node.id);
          const accent = config?.color ?? '#0284c7';
          nodes[i] = {
            ...node,
            style: {
              ...bs,
              opacity: 1,
              boxShadow: `0 0 0 2px ${alpha(accent, 0.22)}, 0 18px 36px ${alpha('#0f172a', 0.16)}`,
              borderColor: accent,
              filter: 'saturate(125%)',
            },
          } as typeof node;
        } else if (node.type !== 'tableGroup') {
          nodes[i] = {
            ...node,
            style: { ...bs, opacity: (typeof bs.opacity === 'number' ? bs.opacity : 1) * 0.2, filter: 'grayscale(75%)' },
          } as typeof node;
        } else {
          nodes[i] = { ...node, style: { ...bs, opacity: (typeof bs.opacity === 'number' ? bs.opacity : 1) * 0.5 } } as typeof node;
        }
      });

      edges.forEach((edge, i) => {
        if (edge.hidden) return;
        const bs = edge.style ? { ...edge.style } : {};
        if (highlightedEdgeIds.has(edge.id)) {
          const config = overlayColorByNodeId.get(edge.source) ?? overlayColorByNodeId.get(edge.target);
          const accent = config?.color ?? '#0284c7';
          const sw = typeof bs.strokeWidth === 'number' ? Math.max(bs.strokeWidth, 3.2) : 3.2;
          edges[i] = { ...edge, animated: true, style: { ...bs, stroke: accent, strokeWidth: sw, opacity: 1 } };
        } else {
          edges[i] = {
            ...edge,
            style: {
              ...bs,
              opacity: (typeof bs.opacity === 'number' ? bs.opacity : 1) * 0.15,
              stroke: bs.stroke ?? '#cbd5f5',
            },
          };
        }
      });
    }

    return { nodes, edges };
  }, [
    canvasGraph,
    canvasViewMode,
    workbookMetadata,
    activeSheetId,
    aggregationOverlayActive,
    aggregationOverlayConfigMap,
    focusedAggregationId,
    layoutDensityConfig,
    tablePreviewSampleOutput,
    expandedGroups,
    showDataPreview,
    canvasSubState,
  ]);

  // ── Canvas Error Toast ──
  useEffect(() => {
    if (canvasGraphError) {
      setToastMessage({ type: 'error', message: toErrorMessage(canvasGraphError, 'Unable to load canvas graph.') });
    }
  }, [canvasGraphError]);

  // ── Preview table ref — lets MappingGroupHeader eye icon trigger preview for specific table ──
  const previewTableRef = useRef<((targetTable: string) => void) | null>(null);
  const handlePreviewTable = useCallback((targetTable: string) => {
    previewTableRef.current?.(targetTable);
  }, []);

  // ── Stable callbacks for MappingTableView (ref-based to prevent React.memo invalidation) ──
  const mappingResponseRef = useRef(mappingResponse);
  mappingResponseRef.current = mappingResponse;
  const handleDeleteMappingRef = useRef(handleDeleteMapping);
  handleDeleteMappingRef.current = handleDeleteMapping;
  const updateMappingMutateRef = useRef(updateMappingMutation.mutate);
  updateMappingMutateRef.current = updateMappingMutation.mutate;

  const handleApproveSelected = useCallback(async (ids: string[]) => {
    if (!batchId) return;
    try {
      await Promise.all(ids.map((id) => apiService.approveMapping(batchId, id)));
      refreshSummary();
      const { mappings, status, recipe_id } = await queryClient.fetchQuery<import('../services/api').MappingResponse>(
        ['mappings', batchId],
        () => apiService.getMappings(batchId)
      );

      if (status !== 'approved' && mappings.every((m: any) => m.approved_at)) {
        await apiService.approveMappings(batchId, recipe_id, 'web-ui');
        refreshSummary();
      }
      await refetchCanvasGraph();
      // Row Status column uses mappingResponse (approved_at / recipe status). Approve-all
      // already refetches ['mappings', batchId]; per-row approve must do the same or UI stays Pending.
      await queryClient.refetchQueries(['mappings', batchId]);
      queryClient.invalidateQueries(['mapping-health', batchId]);
      setToastMessage({ type: 'success', message: `${ids.length} mapping(s) approved.` });
    } catch {
      setToastMessage({ type: 'error', message: 'Unable to approve selected mappings.' });
    }
  }, [batchId, refetchCanvasGraph, queryClient, refreshSummary]);

  const handleDeleteSelected = useCallback((ids: string[]) => {
    const mr = mappingResponseRef.current;
    if (!mr || !Array.isArray(mr.mappings)) return;
    ids.forEach((id) => {
      const mapping = mr.mappings.find((m) => m.mapping_id === id);
      if (mapping) handleDeleteMappingRef.current(mapping);
    });
  }, []);


  const handleInlineEditSave = useCallback((row: import('../canvas/tableView/types').MappingTableRowData, values: import('../canvas/tableView/MappingTableRow').InlineEditValues) => {
    const mr = mappingResponseRef.current;
    if (!mr?.recipe_id || !row.mapping) return;
    const m = row.mapping;
    const payload: import('../services/api').UpdateMappingRequest = {
      recipe_id: mr.recipe_id,
      edited_by: 'web-ui',
    };
    const op = values.operation || values.transformationType || 'direct';
    const valueOnlyOps = new Set(['set_constant', 'set_null', 'constant', 'literal', 'generate_uuid']);
    const isValueOnly = valueOnlyOps.has(op);

    if (op !== (m.operation || '')) payload.operation = op;

    if (isValueOnly) {
      if (op === 'set_constant') {
        const constantValue = (values.constantValue ?? values.transformation ?? '').trim();
        payload.source_file = '__operator_constant__';
        payload.source_field = '';
        payload.transformation_type = 'literal';
        payload.transformation = constantValue;
        payload.step = {
          step_id: m.transformation_steps?.[0]?.step_id ?? m.mapping_id,
          operation: 'set_constant',
          inputs: [],
          outputs: [`${m.target_table}.${m.target_field}`],
          parameters: { value: constantValue },
          confidence: m.confidence,
          ai_generated: false,
        };
      }
    } else {
      if (values.sourceTable !== m.source_file) payload.source_file = values.sourceTable;
      if (values.sourceField !== m.source_field) payload.source_field = values.sourceField;
      if (values.transformationType !== (m.transformation_type || 'direct'))
        payload.transformation_type = values.transformationType;
      if (values.transformation !== (m.transformation || ''))
        payload.transformation = values.transformation || null;
    }

    if (values.hierarchyPath && JSON.stringify(values.hierarchyPath) !== JSON.stringify(m.hierarchy_path || null))
      payload.hierarchy_path = values.hierarchyPath;
    updateMappingMutateRef.current({ mappingId: m.mapping_id, payload });
  }, []);

  const handleSheetNavigatorSelect = useCallback((sheetId: string) => {
    setActiveSheetId(sheetId);
    // Plan §F: sheet switch resets selection so the right Inspector
    // doesn't keep pointing at a now-hidden field.
    clearSelection();
    fieldSelection.clear();
    recordCanvasEvent('canvas.sheet.changed', { sheet_id: sheetId, workbook_id: activeWorkbookId ?? '' });
  }, [recordCanvasEvent, activeWorkbookId, clearSelection, fieldSelection]);

  const handleApproveAll = useCallback(() => approveMappingsMutation.mutate(), [approveMappingsMutation]);
  const handleRefreshGraph = useCallback(() => refetchCanvasGraph(), [refetchCanvasGraph]);
  const handleToggleAggOverlay = useCallback(() => setAggregationOverlayEnabled((p) => !p), [setAggregationOverlayEnabled]);
  const handleToggleGrid = useCallback(() => setShowGrid((p) => !p), []);
  const handleToggleFocus = useCallback(() => setFocusModeEnabled((p) => !p), [setFocusModeEnabled]);

  // ─── Early Returns ──────────────────────────────────────────────────────────
  if (!batchId) return <Alert severity="error">Missing batch identifier.</Alert>;

  if (mappingQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show a generation-in-progress indicator when the batch is still being mapped
  // and we have no real mapping data yet (empty or missing recipe_id).
  const hasNoMappings = !mappingResponse?.recipe_id || mappingResponse.mappings.length === 0;
  const hasNoTargetSchema = !targetSchema?.schema_id;
  if (isMappingInProgress && hasNoMappings) {
    if (hasNoTargetSchema) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
          <Typography variant="h6" color="text.secondary">
            No Target Schema Assigned
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 420, textAlign: 'center' }}>
            Mapping requires a target schema. Assign one to this project via the Transformations page,
            or use the Workspace to map data conversationally.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          AI mapping generation in progress...
        </Typography>
        <Typography variant="body2" color="text.disabled">
          This may take a minute. The results will appear automatically.
        </Typography>
      </Box>
    );
  }

  if (mappingQuery.error) {
    return <Alert severity="error">{toErrorMessage(mappingQuery.error, 'Unable to load mapping data.')}</Alert>;
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <CanvasErrorBoundary>
      <Stack spacing={0} sx={{ height: '100%' }}>
        <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
          <CanvasSheetNavigator
            sheets={(mappingResponse?.metadata?.source_schema_snapshot as any)?.sheets ?? []}
            activeSheetId={activeSheetId}
            onSelect={handleSheetNavigatorSelect}
            dependencyDag={mappingResponse?.metadata?.dependency_dag as Record<string, string[]>}
          />
          {/* Left Sidebar — always visible */}
          <MappingOverviewPanel
            mappingResponse={mappingResponse}
            canvasGraph={canvasGraph}
            focusedTargetIds={focusedTargetIds}
            focusedSourceIds={focusedSourceIds}
            onToggleTarget={handleToggleFocusTarget}
            onToggleSource={handleToggleFocusSource}
            onClearFocus={handleClearFocus}
            availableGroups={availableGroups}
            onFocusNode={handleOverviewFocusNode}
            onSelectEdge={handleOverviewSelectEdgeWrapper}
            canvasViewMode={canvasViewMode}
            canvasSubState={canvasSubState}
            detailTableId={detailTableId}
            detailTableRole={detailTableRole}
            onDrillIntoTable={handleDrillIntoTable}
            onReturnToOverview={handleReturnToOverview}
            onSelectPipelineTarget={setPipelineSelectedTarget}
            pipelineSelectedTarget={pipelineSelectedTarget}
            searchQuery={canvasSearchQuery}
            onSearchQueryChange={setCanvasSearchQuery}
            onBackToPipeline={_onBack}
            reviewItems={reviewBundle.items}
            reviewCounts={reviewBundle.counts}
            activeFieldSelection={fieldSelection.selection}
            onSelectReviewItem={handleReviewItemSelect}
            initialReviewTab={reviewInitialTab}
            decisionKindFallbackCounts={
              ((mappingResponse?.metadata as any)?.validator_summary?.by_kind ?? undefined) as
                | Record<string, number>
                | undefined
            }
            onClearReviewSelection={() => {
              fieldSelection.clear();
              clearSelection();
            }}
            decisionsError={Boolean(pendingDecisionsQuery?.isError)}
            sessionId={_sessionId}
          />
          <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <CanvasToolbar
              mapping={mappingResponse}
              batchStatus={batchStatus}
              destinationStatus={destinationExportStatus ?? undefined}
              workbookId={activeWorkbookId}
              aggregationPlanLength={mappingResponse?.metadata?.aggregation_plan?.length}
              manualAdjustmentCount={mappingResponse?.metadata?.manual_adjustments?.length}
              onGenerate={
                !guardrailStatus.blocked
                  ? handleGenerateDraft
                  : undefined
              }
              generating={generateMappingsMutation.isLoading}
              onApprove={
                !guardrailStatus.blocked && mappingResponse?.status !== 'approved'
                  ? handleApproveAll
                  : undefined
              }
              approving={approveMappingsMutation.isLoading}
              onRefresh={handleRefreshGraph}
              refreshing={canvasGraphFetching}
              onToggleAggregationOverlay={handleToggleAggOverlay}
              aggregationOverlayEnabled={aggregationOverlayEnabled}
              aggregationOverlayAvailable={Boolean(
                mappingResponse?.metadata?.aggregation_plan &&
                mappingResponse.metadata.aggregation_plan.length > 0,
              )}
              projectName={projectSummary?.project?.name}
              schemaSetName={targetSchema?.schema_name}
              schemaSetVersion={targetSchema?.version}
              guardrailWarning={guardrailStatus.message}
              guardrailLabel={guardrailStatus.label}
              editingBlocked={guardrailStatus.blocked}
              onToggleGrid={handleToggleGrid}
              gridEnabled={showGrid}
              focusModeEnabled={focusModeEnabled}
              onToggleFocusMode={handleToggleFocus}
              focusSelectionCount={focusedTargetIds.size + focusedSourceIds.size}
              canvasViewMode={canvasViewMode}
              onChangeViewMode={setCanvasViewMode}
              generationMode={generationMode}
              onChangeGenerationMode={setGenerationMode}
              hasApprovedParent={Boolean(mappingResponse?.metadata?.parent_recipe_id)}
              isApproved={mappingResponse?.status === 'approved'}
              onLoadData={handleLoadData}
              loadingData={loadingData}
              executionCount={mappingResponse?.metadata?.execution_count as number | undefined}
              sessionId={_sessionId}
              batchId={batchId}
              previewTableRef={previewTableRef}
              healthIssues={healthQuery.data?.issues}
            />
            <Backdrop
              open={generateMappingsMutation.isLoading}
              sx={(theme) => ({
                color: '#fff',
                zIndex: theme.zIndex.drawer + 2,
                flexDirection: 'column',
              })}
            >
              <CircularProgress color="inherit" />
              <Typography variant="body1" sx={{ mt: 2 }}>
                AI mapping regeneration in progress
              </Typography>
            </Backdrop>
            <SchemaDiffBanner
              mappingResponse={mappingResponse}
              onReviewBroken={handleReviewBroken}
              onReviewStale={handleReviewStale}
            />
            {/* Phase 0d: Show filter banner when table selection is active */}
            {(() => {
              if (!srcFilter?.length && !tgtFilter?.length) return null;
              // Derive total table counts from the schema snapshot (all tables in the schema set).
              const snapshot = mappingResponse?.transformation_plan?.target_schema_snapshot
                ?? (mappingResponse?.metadata as any)?.target_schema_snapshot;
              const totalTargets = snapshot?.tables ? Object.keys(snapshot.tables).length : 0;
              const totalSources = mappingResponse?.dependent_sources?.length
                ?? (mappingResponse?.mappings ? new Set(mappingResponse.mappings.map((m) => m.source_file)).size : 0);
              return (
                <Alert severity="info" variant="outlined" sx={{ mb: 1, py: 0.25 }}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" fontWeight={600}>Filtered view:</Typography>
                    {tgtFilter && tgtFilter.length > 0 && (
                      <Chip
                        size="small"
                        label={totalTargets ? `Showing ${tgtFilter.length} of ${totalTargets} target tables` : `${tgtFilter.length} target table${tgtFilter.length > 1 ? 's' : ''}`}
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    {srcFilter && srcFilter.length > 0 && (
                      <Chip
                        size="small"
                        label={totalSources ? `Showing ${srcFilter.length} of ${totalSources} source tables` : `${srcFilter.length} source table${srcFilter.length > 1 ? 's' : ''}`}
                        color="info"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    <Link
                      component="button"
                      variant="caption"
                      underline="hover"
                      onClick={handleClearFocus}
                      sx={{ ml: 0.5, cursor: 'pointer', fontSize: '0.65rem' }}
                    >
                      Show all
                    </Link>
                  </Stack>
                </Alert>
              );
            })()}
            {canvasViewMode === 'mapping-table' ? (
              <MappingTableView
                mappingResponse={mappingResponse}
                onSelectionChange={handleSelectionChange}
                externalSearchQuery={canvasSearchQuery}
                selectedTargetIds={focusedTargetIds}
                selectedSourceIds={focusedSourceIds}
                healthIssues={healthQuery.data?.issues}
                onApproveSelected={handleApproveSelected}
                onDeleteSelected={handleDeleteSelected}
                onAiRegenSelected={() => { }}
                onInlineEditSave={handleInlineEditSave}
                onPreview={handlePreviewTable}
                outputStructure={targetSchema?.definition?.output_structure}
                fieldSelectionRowId={
                  fieldSelection.selection?.scope === 'field'
                    ? `${fieldSelection.selection.target_table}.${fieldSelection.selection.target_field}`.toLowerCase()
                    : null
                }
                reviewItemsByField={reviewBundle.byField}
              />
            ) : canvasSubState === 'overview' ? (
              <PipelineView
                mappingResponse={mappingResponse}
                sqlByTarget={sqlPanelData}
                onDrillIntoTable={handleDrillIntoTable}
                selectedTarget={pipelineSelectedTarget}
                onSelectTarget={setPipelineSelectedTarget}
                selectedTargetIds={focusedTargetIds}
                selectedSourceIds={focusedSourceIds}
              />
            ) : (
              <GraphCanvas
                graph={displayedGraph}
                onSelectionChange={handleSelectionChange}
                onPaneClick={clearSelection}
                onConnect={handleCanvasConnect}
                loading={canvasGraphFetching}
                showGrid={showGrid}
                revision={graphRevision}
                focusModeEnabled={focusModeEnabled}
                focusNodeId={focusNodeId}
                fitViewTrigger={fitViewTrigger}
                highlightNodeIds={canvasHighlightNodeIds}
                overlay={<Box />}
                onNodeContextMenu={handleNodeContextMenu}
              />
            )}
          </Box>
          {!embedded && !(canvasViewMode === 'canvas' && canvasSubState === 'overview') && (
            <PreviewPanel
              open={tablePreviewOpen || (canvasSubState === 'detail' && selection != null) || (canvasViewMode === 'mapping-table' && selection != null)}
              loading={tablePreviewLoading}
              error={tablePreviewError}
              data={tablePreviewResponse}
              notice={tablePreviewNotice}
              nodeLabel={
                selection?.kind === 'node'
                  ? (selection.node.data.label || selection.node.data.field || selection.node.data.qualifiedName || 'Node')
                  : selection?.kind === 'edge'
                    ? (selection.edge.data?.label || 'Edge')
                    : (tablePreviewTarget?.label ?? 'Table')
              }
              nodeData={selection?.kind === 'node' ? selection.node.data : undefined}
              onClose={() => {
                handleCloseTablePreview();
                clearSelection();
              }}
              onOpenDataPreview={() => setDataPreviewModalOpen(true)}
              onRefresh={
                tablePreviewTarget ? () => handleOpenTablePreview(tablePreviewTarget) : undefined
              }
              promptRuns={promptRuns}
              manualAdjustments={manualAdjustments}
              edgeFieldMap={edgeFieldMap}
              onUndoAdjustment={handleUndoAdjustment}
              onRedoAdjustment={handleRedoAdjustment}
              undoingAdjustmentId={undoingAdjustmentId}
              redoingAdjustmentId={redoingAdjustmentId}
              selection={selection}
              mappingResponse={mappingResponse}
              onSaveMapping={handleSave}
              isSavingMapping={updateMappingMutation.isLoading || isCreatingMapping}
              onCreateMapping={handleCreateMapping}
              onRegenerateMapping={handleRegenerateTransformation}
              isRegeneratingMapping={isRegeneratingMapping}
              onValidateMapping={handleValidateTransformation}
              isValidatingMapping={isValidatingMapping}
              onDeleteMapping={handleDeleteMapping}
              isDeletingMapping={deleteMappingMutation.isLoading}
              computedUpstream={computedLineage.upstream}
              computedDownstream={computedLineage.downstream}
              lineageChains={computedLineageChains}
              healthData={healthQuery.data}
              healthLoading={healthQuery.isLoading}
              healthError={healthQuery.isError}
              onApplyFix={handleApplyFix}
              batchId={batchId}
              fieldReviewItems={fieldScoped.fieldReviewItems}
              fieldValidatorRules={fieldScoped.fieldValidatorRules}
              fieldValidatorOverrides={fieldScoped.fieldValidatorOverrides}
              fieldIsPrimaryKey={fieldScoped.fieldIsPrimaryKey}
              overriddenRuleNames={fieldScoped.overriddenRuleNames}
              sampleOutput={mappingResponse?.sample_output}
              onMaterializeSamples={handleMaterializeSamples}
              materializing={materializing}
              resolveDisabled={Boolean(guardrailStatus?.blocked)}
              onSelectInlineEdit={handleSelectInlineEdit}
            />
          )}
        </Stack>


        <DeleteMappingDialog
          open={deleteMappingDialog.open}
          mapping={deleteMappingDialog.mapping}
          deleting={deleteMappingMutation.isLoading}
          onConfirm={handleConfirmDeleteMapping}
          onCancel={() => setDeleteMappingDialog({ open: false, mapping: null })}
        />

        {/* Phase 4.7: Type Mismatch Popover */}
        <TypeMismatchPopover
          open={Boolean(typeMismatch?.open)}
          anchorEl={typeMismatch?.anchorEl ?? null}
          onClose={() => setTypeMismatch(null)}
          sourceType={typeMismatch?.sourceType ?? ''}
          targetType={typeMismatch?.targetType ?? ''}
          sourceField={typeMismatch?.sourceField ?? ''}
          targetField={typeMismatch?.targetField ?? ''}
          onApplyCast={handleTypeMismatchApplyCast}
          onProceed={handleTypeMismatchProceed}
        />

        {/* Data Preview Modal */}
        <DataPreviewModal
          open={dataPreviewModalOpen}
          onClose={() => setDataPreviewModalOpen(false)}
          loading={tablePreviewLoading}
          error={tablePreviewError}
          data={tablePreviewResponse}
          notice={tablePreviewNotice}
          nodeLabel={tablePreviewTarget?.label ?? 'Table'}
          onRefresh={
            tablePreviewTarget ? () => handleOpenTablePreview(tablePreviewTarget) : undefined
          }
          violations={healthQuery.data?.issues?.filter(i => i.row_indices && i.row_indices.length > 0)}
        />

        {/* Phase 4.2: Generated SQL Panel */}
        <GeneratedSqlPanel
          open={sqlPanelOpen}
          onClose={() => setSqlPanelOpen(false)}
          sqlByTarget={sqlPanelData}
        />

        {/* Phase 4.3: Join Builder Modal */}
        <JoinBuilderModal
          open={joinBuilderOpen}
          onClose={() => setJoinBuilderOpen(false)}
          sourceTables={
            availableGroups
              .filter(g => g.role === 'source')
              .map(g => g.id)
          }
          columnsByTable={{}}
          onSubmit={() => {
            setJoinBuilderOpen(false);
            setToastMessage({ type: 'success', message: 'Join configuration saved.' });
          }}
        />

        {/* Phase 4.4: Aggregation Builder Modal */}
        <AggregationBuilderModal
          open={aggBuilderOpen}
          onClose={() => setAggBuilderOpen(false)}
          targetTables={
            availableGroups
              .filter(g => g.role === 'target')
              .map(g => g.id)
          }
          availableColumns={[]}
          onSubmit={() => {
            setAggBuilderOpen(false);
            setToastMessage({ type: 'success', message: 'Aggregation configuration saved.' });
          }}
        />

        {/* Phase 4.8: Canvas Context Menu */}
        <CanvasContextMenu
          position={contextMenu ? { x: contextMenu.mouseX, y: contextMenu.mouseY } : null}
          node={contextMenu?.node}
          onClose={handleContextMenuClose}
          onEditTransformation={() => {
            if (contextMenu?.node) {
              handleSelectionChange({ kind: 'node', node: contextMenu.node });
            }
            handleContextMenuClose();
          }}
          onAiRegenerate={() => {
            handleContextMenuClose();
          }}
          onApprove={() => {
            handleContextMenuClose();
            setToastMessage({ type: 'success', message: 'Approval requested.' });
          }}
          onDelete={() => {
            handleContextMenuClose();
            if (contextMenu?.node?.data?.mapping) {
              handleDeleteMapping(contextMenu.node.data.mapping);
            }
          }}
          onToggleExpand={() => {
            if (contextMenu?.node?.id) {
              handleToggleGroup(contextMenu.node.id);
            }
          }}
          onExpandAll={() => {
            const allIds = new Set(allGroupIds);
            setExpandedGroups(allIds);
          }}
          onCollapseAll={() => {
            setExpandedGroups(new Set());
          }}
          onPreviewTable={() => {
            if (contextMenu?.node) {
              const nd = contextMenu.node.data;
              const displayLabel = nd?.label?.trim() || nd?.qualifiedName || nd?.table || 'Table';
              const candidateValues = Array.from(
                new Set(
                  [nd?.qualifiedName, nd?.table, nd?.label].filter(
                    (v): v is string => Boolean(v && v.trim().length > 0),
                  ),
                ),
              );
              handleOpenTablePreview({
                tableKey: candidateValues[0],
                label: displayLabel,
                role: nd?.groupRole,
                sheetId: nd?.sheetId,
                candidates: candidateValues,
              });
            }
          }}
        />

        <Snackbar
          open={Boolean(toastMessage)}
          autoHideDuration={4000}
          onClose={() => setToastMessage(null)}
          message={toastMessage?.message}
        />

        {/* Add Source dialog triggered by persistent canvas action node */}
        {selectedProjectId && (
          <AddSourceDialog
            open={addSourceDialogOpen}
            onClose={handleCloseAddSourceDialog}
            projectId={selectedProjectId}
            sessionId={_sessionId ?? null}
          />
        )}
      </Stack>
    </CanvasErrorBoundary>
  );
};

export default CanvasMappingEditor;
